import { useMemo } from 'react'
import {
  analisarTalabartes,
  DOBRAS_NO_ALTO,
  MATERIAIS_TALABARTE,
  REFERENCIAS_ALTURA,
  resumirQueda,
  type ChoqueDoTalabarte,
  AVISO_VALORES_PROVISORIOS,
  HA_REFERENCIA_NAO_CONFIRMADA,
  type CenaTabletop,
  type ObjetoTabletop,
} from '../../dominio/tabletop'
import { useQuedaAnimada } from './useQuedaAnimada'
import { GraficoForca } from './GraficoForca'
import { ComparacaoQueda, descricaoDaReferencia } from './ComparacaoQueda'
import { EnsaioDeReferencia } from './EnsaioDeReferencia'

const virgula = (valor: number, casas = 2) => valor.toFixed(casas).replace('.', ',')

/**
 * Inspetor do trabalhador em altura: para cada talabarte dele, o fator de
 * queda, a queda livre e a zona livre de queda, parcela por parcela, e se a
 * queda caberia sobre a superfície abaixo. As medidas do talabarte, do
 * absorvedor, do engate e da margem são parâmetros do trabalhador, acima.
 */
export function InspetorQueda({
  cena,
  objeto,
  aoResetarSoltura,
}: {
  cena: CenaTabletop
  objeto: ObjetoTabletop
  aoResetarSoltura?: (id: string) => void
}) {
  const talabartes = analisarTalabartes(cena).filter((t) => t.idTrabalhador === objeto.id)
  const nomeDoAlvo = (idObjeto: string) => cena.objetos.find((o) => o.id === idObjeto)?.nome ?? idObjeto

  return (
    <fieldset className="inspetor-vitima inspetor-corda">
      <legend>Queda em altura</legend>
      {objeto.posicaoDeSoltura && aoResetarSoltura && (
        <div className="acoes-linha" style={{ marginBottom: '0.5rem' }}>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={() => aoResetarSoltura(objeto.id)}
          >
            Resetar para posição de soltura
          </button>
        </div>
      )}
      {talabartes.length === 0 ? (
        <div>
          <p className="dica">
            Sem talabarte. Use a ferramenta de corda com a função "Talabarte do trabalhador": clique no engate dorsal do trabalhador e
            depois no ponto de ancoragem.
          </p>
          <SimulacaoDaQueda idTrabalhador={objeto.id} cena={cena} />
        </div>
      ) : (
        talabartes.map((t) => {
          const r = t.resultado
          return (
            <div key={t.idCorda} aria-live="polite">
              <p className="corda-resumo">
                <strong>{r.baterNoChao ? 'A queda não cabe' : 'A queda cabe'}</strong>
                <span className="dica"> · preso em {nomeDoAlvo(t.ancoragem.idObjeto)}</span>
              </p>
              {!r.alcanca && (
                <p className="aviso-corda" role="alert">
                  O talabarte não alcança: a ancoragem fica a {virgula(r.distanciaM)} m do engate e ele tem {virgula(r.parcelasZlq.talabarteM)} m.
                </p>
              )}
              <ul className="lista-pontos-ancoragem">
                <li>
                  Fator de queda: {virgula(r.fatorDeQueda)}
                  {r.fatorDesfavoravel ? ' (ancoragem abaixo do engate: desfavorável)' : ''}
                </li>
                <li>Queda livre: {virgula(r.quedaLivreM)} m</li>
                <li>
                  Zona livre de queda: {virgula(r.zonaLivreM)} m = talabarte {virgula(r.parcelasZlq.talabarteM)} + absorvedor{' '}
                  {virgula(r.parcelasZlq.absorvedorM)} + engate aos pés {virgula(r.parcelasZlq.engateAosPesM)} + margem{' '}
                  {virgula(r.parcelasZlq.margemM)}
                </li>
                <li>Altura livre da ancoragem até a superfície abaixo: {virgula(r.alturaLivreM)} m</li>
                <li>
                  {r.baterNoChao ? `Faltam ${virgula(-r.folgaM)} m: bateria antes de parar` : `Sobram ${virgula(r.folgaM)} m`}
                </li>
              </ul>
              <ForcaDeChoque
                choque={t.choque}
                aberturaMaximaM={r.parcelasZlq.absorvedorM}
                sobraComMargemM={r.baterNoChao ? null : r.folgaM + r.parcelasZlq.margemM}
              />
              <SimulacaoDaQueda idCorda={t.idCorda} cena={cena} />
            </div>
          )
        })
      )}
      <EnsaioDeReferencia objeto={objeto} />
      <p className="dica">
        Modelo didático da queda na vertical da ancoragem, sem pêndulo. A superfície abaixo é o primeiro obstáculo de onde ele pode
        cair, dentro do alcance do talabarte: o chão além da borda ou, dentro da torre, o patamar de baixo.
      </p>
      {HA_REFERENCIA_NAO_CONFIRMADA && (
        <p className="aviso-corda" role="note">
          {AVISO_VALORES_PROVISORIOS}
        </p>
      )}
    </fieldset>
  )
}

/**
 * O pico de força que o talabarte passa ao corpo e à ancoragem ao parar a
 * queda, quanto ele estica e quanto o absorvedor abre.
 */
function ForcaDeChoque({
  choque,
  aberturaMaximaM,
  sobraComMargemM,
}: {
  choque: ChoqueDoTalabarte
  aberturaMaximaM: number
  /** O que sobra abaixo dos pés contando a margem; `null` se a zona livre já diz que bate. */
  sobraComMargemM: number | null
}) {
  const r = choque.resultado
  const material = MATERIAIS_TALABARTE[choque.material].nome
  const limite = REFERENCIAS_ALTURA.forcaMaximaNoCorpoKN.valor
  const redutor = choque.redutor.nomeCurto
  const abre = choque.redutor.tipo === 'absorvedor' ? 'abre' : 'escorrega'
  const abrir = choque.redutor.tipo === 'absorvedor' ? 'abrir' : 'escorregar'
  return (
    <div className="forca-de-choque">
      <p className="corda-resumo">
        <strong>Força de choque: {virgula(r.forcaPicoKN, 1)} kN</strong>
        <span className="dica">
          {choque.dobra === 'direta'
            ? ' · no corpo e na ancoragem, que recebe o talabarte direto'
            : ` · no corpo; a ancoragem recebe ${virgula(choque.forcaNaAncoragemKN, 1)} kN`}
        </span>
      </p>
      {choque.acimaDoLimiteDoCorpo && (
        <p className="aviso-corda" role="alert">
          Passa dos {virgula(limite, 0)} kN de referência no corpo.
        </p>
      )}
      <ul className="lista-pontos-ancoragem">
        <li>
          {choque.massaKg} kg ({virgula(r.pesoKN, 2)} kN de peso): o pico é {virgula(r.forcaPicoKN / r.pesoKN, 1)} vezes o peso
        </li>
        <li>
          O talabarte de {material} estica {virgula(r.alongamentoNoPicoM)} m no pico
        </li>
        {choque.dobra !== 'direta' && (
          <li>
            {DOBRAS_NO_ALTO[choque.dobra].nome}: a corda dobra e a ancoragem recebe os dois ramos, {virgula(choque.fatorNaAncoragem, 2)} vezes a
            força no corpo ({virgula(choque.forcaNaAncoragemKN, 1)} kN)
          </li>
        )}
        {r.regime === 'elastico' && aberturaMaximaM > 0 && <li>A queda é pequena: o {redutor} nem chega a {abrir}</li>}
        {r.regime === 'elastico' && aberturaMaximaM <= 0 && <li>Sem absorvedor: só o talabarte segura a queda</li>}
        {choque.alemDaZonaLivreM > 0.005 && (
          <li>
            Depois de esticar, o corpo desce {virgula(r.descidaAposEsticarM)} m: {virgula(choque.alemDaZonaLivreM)} m além da abertura do
            {redutor} que a zona livre considera, tirados da margem de segurança
          </li>
        )}
        {r.regime === 'absorvedor' && (
          <li>
            O {redutor} {abre} {virgula(r.aberturaAbsorvedorM)} m de{' '}
            {virgula(aberturaMaximaM)} m e segura a força em{' '}
            {virgula(r.forcaPicoKN, 1)} kN
          </li>
        )}
      </ul>
      {r.regime === 'absorvedor-esgotado' && (
        <p className="aviso-corda" role="alert">
          {r.freioMaisFracoQueOPeso
            ? `A força de frenagem do ${redutor} é menor que o peso (${virgula(r.pesoKN, 1)} kN): ele ${abre} todo (${virgula(aberturaMaximaM)} m) e a força volta a subir no talabarte.`
            : `O ${redutor} ${abre} todo (${virgula(aberturaMaximaM)} m) e não basta: a força volta a subir no talabarte.`}
        </p>
      )}
      {choque.alemDaZonaLivreM > 0.005 && sobraComMargemM !== null && choque.alemDaZonaLivreM > sobraComMargemM && (
        <p className="aviso-corda" role="alert">
          Com o talabarte esticando, a queda passa da margem de segurança e bateria antes de parar.
        </p>
      )}
      <p className="dica">
        Modelo didático: o talabarte é uma mola que só puxa, e o redutor de força (absorvedor, Prusik ou freio dinâmico), um freio de força constante. Nós, costuras e o próprio
        corpo também absorvem energia, e o material real endurece com a carga. A rigidez de cada material é provisória.
      </p>
    </div>
  )
}

const MENSAGEM_SEM_QUEDA: Readonly<Record<'apoiado' | 'nao-alcanca', string>> = {
  apoiado:
    'O talabarte segura o trabalhador antes de ele sair do apoio: não há queda. Leve o trabalhador para perto da borda para ver a queda.',
  'nao-alcanca': 'O talabarte não alcança a ancoragem: o trabalhador não está preso, e não há o que animar.',
}

/** O botão "Simular queda", a régua de tempo e o resumo do que aconteceu. */
function SimulacaoDaQueda({ idCorda, idTrabalhador, cena }: { idCorda?: string; idTrabalhador?: string; cena: CenaTabletop }) {
  const q = useQuedaAnimada()
  const resultado = idCorda
    ? (q.resultado?.idCorda === idCorda ? q.resultado : null)
    : (q.resultado && q.resultado.idTrabalhador === idTrabalhador && !q.resultado.idCorda ? q.resultado : null)
  const quadros = resultado?.quadros.length ?? 0
  const atual = useMemo(() => (resultado ? resumirQueda(cena, resultado) : null), [cena, resultado])
  const quadro = resultado && quadros > 0 ? resultado.quadros[Math.min(q.indice, quadros - 1)]! : null
  const tempoNaCurva = resultado && quadro && quadro.fase !== 'queda-livre' ? quadro.t - resultado.inicioDaFrenagemS : null

  return (
    <div className="simulacao-fisica">
      {!resultado && (
        <p className="dica">
          {idCorda
            ? 'Puxe o trabalhador: arraste-o na cena para longe da ancoragem ou para a borda, e veja o fator de queda e a força mudarem. Depois solte com "Simular a queda".'
            : 'Trabalhador sem talabarte: arraste-o na cena e simule a queda livre para verificar o ponto e velocidade de impacto no solo ou patamar.'}
        </p>
      )}
      <div className="acoes-linha">
        {idCorda ? (
          <>
            <button type="button" className="btn-acao-cad btn-acao-cad--destaque" onClick={() => q.simular(idCorda)}>
              Simular a queda
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              disabled={q.estadoMotor === 'carregando'}
              onClick={() => void q.simularComFisica(idCorda)}
            >
              Simular com física
            </button>
          </>
        ) : idTrabalhador ? (
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--destaque"
            disabled={q.estadoMotor === 'carregando'}
            onClick={() => void q.simularQuedaLivre(idTrabalhador)}
          >
            Simular queda livre (sem talabarte)
          </button>
        ) : null}
        {resultado && quadros > 1 && (
          <>
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={q.alternarReproducao}>
              {q.tocando ? 'Pausar' : 'Reproduzir'}
            </button>
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={q.limpar}>
              Limpar
            </button>
          </>
        )}
        {atual && (
          <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={q.guardarReferencia}>
            {q.referencia ? 'Trocar a referência por esta queda' : 'Guardar como referência'}
          </button>
        )}
      </div>
      {q.estadoMotor === 'carregando' && (
        <p className="dica" role="status">
          Carregando o motor de física…
        </p>
      )}
      {q.estadoMotor === 'indisponivel' && (
        <p className="aviso-corda" role="alert">
          O motor de física não carregou. A queda calculada continua disponível.
        </p>
      )}
      {q.referencia && (
        <p className="dica">
          Referência: {descricaoDaReferencia(q.referencia)}.{' '}
          <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={q.limparReferencia}>
            Tirar a referência
          </button>
        </p>
      )}
      {!q.referencia && !atual && resultado === null && (
        <p className="dica">Para comparar: simule uma queda, guarde como referência, mude uma coisa e simule de novo.</p>
      )}
      {resultado && resultado.situacao !== 'ok' && (
        <p className="aviso-corda" role="alert">
          {MENSAGEM_SEM_QUEDA[resultado.situacao]}
        </p>
      )}
      {resultado && resultado.situacao === 'ok' && quadros > 0 && (
        <div aria-live="polite">
          <label className="campo">
            <span>Tempo da queda</span>
            <input
              type="range"
              aria-label="Tempo da queda"
              min={0}
              max={quadros - 1}
              value={Math.min(q.indice, quadros - 1)}
              onChange={(e) => q.definirIndice(Number(e.target.value))}
            />
            <span className="dica">
              {virgula(quadro!.t, 2)} s · {virgula(quadro!.forcaKN, 1)} kN no talabarte
            </span>
          </label>
          {resultado.rompimento && (
            <p className="aviso-corda aviso-corda--rompimento" role="alert">
              <strong>Ruptura de talabarte:</strong> a força atingiu {virgula(resultado.rompimento.forcaKN, 1)} kN
              {resultado.rompimento.cargaRupturaKN !== undefined && ` (limite de ${virgula(resultado.rompimento.cargaRupturaKN, 1)} kN)`}. A corda partiu aos {virgula(resultado.rompimento.t, 2)} s e o
              trabalhador continuou em queda livre.
            </p>
          )}
          {resultado.curvaDeForca && resultado.choque && (
            <>
              <GraficoForca
                curva={resultado.curvaDeForca}
                pesoKN={resultado.choque.pesoKN}
                limiteKN={REFERENCIAS_ALTURA.forcaMaximaNoCorpoKN.valor}
                tempoS={tempoNaCurva}
                referencia={q.referencia?.curva}
              />
              {q.referencia && (
                <p className="grafico-forca__legenda">
                  <span>
                    <span className="grafico-forca__chave" aria-hidden="true" />
                    Esta queda
                  </span>
                  <span>
                    <span className="grafico-forca__chave grafico-forca__chave--referencia" aria-hidden="true" />
                    Referência
                  </span>
                </p>
              )}
              <p className="dica">
                Do talabarte esticar ao ponto mais baixo: {Math.round(resultado.curvaDeForca.duracaoS * 1000)} ms e{' '}
                {virgula(resultado.choque.descidaAposEsticarM)} m de descida, com pico de {virgula(resultado.choque.forcaPicoKN, 1)} kN.
              </p>
            </>
          )}
          {q.referencia && atual && <ComparacaoQueda referencia={q.referencia} atual={atual} />}
          <ul className="lista-pontos-ancoragem">
            {resultado.idCorda ? (
              <>
                <li>
                  Queda livre de {virgula(resultado.quedaLivreM)} m até o talabarte esticar, a {virgula(resultado.velocidadeAoEsticarMs, 1)} m/s
                  ({virgula(resultado.velocidadeAoEsticarMs * 3.6, 0)} km/h)
                </li>
                <li>Pêndulo de {virgula(resultado.anguloDoPenduloGraus, 0)}° em relação à vertical da ancoragem</li>
              </>
            ) : (
              <li>
                Queda livre solto no ar: aceleração da gravidade até colidir com a superfície ou estrutura
              </li>
            )}
            {resultado.simulado && resultado.choque && (
              <li>
                Pico simulado: <strong>{virgula(resultado.simulado.picoKN, 1)} kN</strong>; o cálculo na vertical dá{' '}
                {virgula(resultado.choque.forcaPicoKN, 1)} kN
                {Math.abs(resultado.simulado.picoKN - resultado.choque.forcaPicoKN) / resultado.choque.forcaPicoKN > 0.02 &&
                  ': com o balanço, parte da energia da queda fica no balanço e não na corda'}
              </li>
            )}
            {resultado.rompimento ? (
              <li>
                <strong>Corda rompida{resultado.rompimento.alturaM !== undefined ? ` a ${virgula(resultado.rompimento.alturaM, 2)} m de altura` : ''}</strong>
                {resultado.impacto && ` — bate em ${resultado.impacto.alvo} a ${virgula(resultado.impacto.velocidadeMs, 1)} m/s`}
              </li>
            ) : resultado.impacto ? (
              <li>
                <strong>Bate em {resultado.impacto.alvo}</strong> a {virgula(resultado.impacto.velocidadeMs, 1)} m/s,{' '}
                {resultado.impacto.fase === 'pendulo' ? 'no balanço' : 'durante a queda'}
              </li>
            ) : (
              <li>Para pendurado, sem bater, com os pés a {virgula(resultado.pesFinaisY ?? 0)} m de altura</li>
            )}
          </ul>
          {resultado.origem === 'fisica' ? (
            <p className="dica">
              Simulação com o motor de física: o corpo cai preso à corda (uma mola que só puxa, em série com o redutor), freia e balança
              em arco, sem fórmula de pêndulo. Os montantes da torre, além do terreno e dos patamares, bloqueiam o balanço. O corpo é um
              segmento vertical; o balanço é amortecido por arrasto, didático.
            </p>
          ) : (
            <p className="dica">
              Animação didática, sem motor de física: queda livre, frenagem pela força de choque calculada (talabarte esticando e
              absorvedor abrindo o que a energia pede) e pêndulo amortecido. A zona livre usa a abertura máxima do absorvedor, o pior
              caso; a queda animada usa a abertura desta queda. O corpo é um segmento vertical, e só o terreno conta como estrutura (a
              torre não bloqueia o balanço). Para ver o balanço bater nos montantes, use "Simular com física".
            </p>
          )}
        </div>
      )}
    </div>
  )
}
